import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiEye, FiEdit, FiTrash2 } from "react-icons/fi";

import { fetchProjectsApi } from "../../services/api/boq/projectService";
import { fetchBoqByProject } from "../../services/api/boq/boqService";

export default function BoqList() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]); // array of projects
  const [selectedProject, setSelectedProject] = useState("");
  const [boqs, setBoqs] = useState([]); // array of BOQs
  const [loading, setLoading] = useState(false);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await fetchProjectsApi();
      // Paginated API: res.data.data.data
      const projectsArray = res?.data?.data?.data || [];
      setProjects(projectsArray);
    } catch (err) {
      toast.error("Failed to load projects");
      setProjects([]);
    }
  };

  // Load BOQs for selected project
  const loadBoqs = async (projectId) => {
    setLoading(true);
    try {
      const res = await fetchBoqByProject(projectId);
      console.log("BOQ API response:", res);

      // If API directly returns array
      if (Array.isArray(res)) {
        setBoqs(res);
      }
      // If API returns inside data
      else if (Array.isArray(res?.data)) {
        setBoqs(res.data);
      } else {
        setBoqs([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load BOQs");
      setBoqs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setSelectedProject(projectId);

    if (projectId) loadBoqs(projectId);
    else setBoqs([]);
  };

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-lg sm:text-xl font-semibold mb-4">BOQ List</h1>

      {/* Project Dropdown */}
      <div className="mb-4 max-w-sm">
        <select
          className="w-full border rounded-lg px-3 py-2 text-sm sm:text-base"
          value={selectedProject}
          onChange={handleProjectChange}
        >
          <option value="">Select Project</option>
          {Array.isArray(projects) &&
            projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.project_name}
              </option>
            ))}
        </select>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
        <h2 className="text-lg sm:text-xl font-semibold">BOQs</h2>
        {selectedProject && (
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm sm:text-base w-full sm:w-auto"
            onClick={() => navigate(`/projects/${selectedProject}/boq/create`)}
          >
            + Create BOQ
          </button>
        )}
      </div>

      {/* Loading / Empty */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : boqs.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl shadow">
          {selectedProject
            ? "No BOQs found for this project"
            : "Select a project to view BOQs"}
        </div>
      ) : (
        <>
          {/* ================= MOBILE CARDS ================= */}
          <div className="grid gap-4 sm:hidden">
            {boqs.map((boq) => (
              <div
                key={boq.id}
                className="bg-white rounded-xl shadow p-4 space-y-2"
              >
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold">{boq.boq_name}</h3>
                  <span className="text-xs px-2 py-1 rounded bg-gray-100">
                    {boq.status}
                  </span>
                </div>

                <p className="text-sm text-gray-600">
                  Discipline: <strong>{boq.discipline}</strong>
                </p>

                <div className="flex gap-4 pt-2">
                  <button
                    onClick={() =>
                      navigate(`/boqs/${boq.id}`, {
                        state: { boqName: boq.boq_name },
                      })
                    }
                    className="text-blue-600 flex items-center gap-1"
                  >
                    <FiEye /> View
                  </button>
                  <button
                    onClick={() => navigate(`/boqs/edit/${boq.id}`)}
                    className="text-green-600 flex items-center gap-1"
                  >
                    <FiEdit /> Edit
                  </button>
                  <button className="text-red-600 flex items-center gap-1">
                    <FiTrash2 /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ================= DESKTOP TABLE ================= */}
          <div className="hidden sm:block bg-white rounded-xl shadow overflow-x-auto">
            <table className="min-w-full text-sm sm:text-base">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">BOQ Name</th>
                  <th className="px-4 py-3 text-left">Discipline</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>

              <tbody>
                {boqs.map((boq, index) => (
                  <tr key={boq.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2">{index + 1}</td>
                    <td className="px-4 py-2">{boq.boq_name}</td>
                    <td className="px-4 py-2">{boq.discipline}</td>
                    <td className="px-4 py-2">{boq.status}</td>
                    <td className="px-4 py-2 flex gap-3">
                      <button
                        onClick={() => navigate(`/boqs/${boq.id}`)}
                        className="text-blue-600"
                      >
                        <FiEye />
                      </button>
                      <button
                        onClick={() => navigate(`/boqs/edit/${boq.id}`)}
                        className="text-green-600"
                      >
                        <FiEdit />
                      </button>
                      <button className="text-red-600">
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
